<template>
  <v-dialog 
    v-if="donation && donation.donor" 
    v-model="errorDialog" 
    max-width="600px" 
    persistent 
    :location="isSmallScreen ? 'bottom' : 'center'"
    :class="isSmallScreen ? 'small-screen-dialog' : ''"
    >
    <v-card>
      <v-card-title class="headline primary white--text text--lighten-5">
        <span class="font-weight-bold">{{ t('paylink.error.errorMessageCouldNotBeDelivered') }}</span>
        <v-spacer></v-spacer>
        <v-btn
          icon
          variant="text"
          size="small"
          @click="errorDialog = false"
          :aria-label="t('common.close')"
        >
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>
      <v-card-text class="body-wrap pa-4">
        <p class="heading">
          <b>{{ t('paylink.error.message', { error: errorMessage }) }}</b>
        </p>
        <br>

        <p class="body-2">
          {{ t('paylink.error.tryAgainOrChooseAnotherPaymentMethod') }}
        </p>
        <br>
        <p class="body-2" v-html="supportContactInfo"></p>
        <br>
        <p class="body-2" style="color: #6B7280;margin-bottom: 5px;">{{ t('donorDetails.form.mobileNumber.label') }}</p>
        <!-- <option-label
          class="mb-0"
          :title="t('donorDetails.form.mobileNumber.label')"
        /> -->
        <v-slide-y-transition>
          
          <v-row>
            <v-col sm="12" xs="12" md="12">
              <v-form
                ref="formMobileNumber"
                v-model="valid"
                data-cy="paylink-retry-mobilenumber-form"
                lazy-validation
              >
                <v-text-field
                  v-model="mobileNumber"
                  density="compact"
                  :hint="t('donationDetails.form.mobileNumber.hint')"
                  :persistent-hint="true"
                  :rules="[
                    rules.mobilePhoneNumber,
                    requiredField('donorDetails.form.mobileNumber.label'),
                  ]"
                  :validate-on-blur="true"
                  data-cy="summary-view-mobilenumber"
                  required
                  clearable
                />
              </v-form>
            </v-col>
          </v-row>
        </v-slide-y-transition>
      </v-card-text>

      <v-divider />

      <v-card-actions class="modal-actions">
        <v-row class="w-100" dense>
          <v-col cols="12" sm="6">
            <v-btn
              block
              color="primary"
              variant="outlined"
              :aria-label="t('paylink.error.chooseAnotherPaymentMethod')"
              @click="goToPaymentMethodSelection"
              class="btn-wrap"
            >
              {{ t('paylink.error.chooseAnotherPaymentMethod') }}
            </v-btn>
          </v-col>

          <v-col cols="12" sm="6">
            <v-btn
              block
              variant="flat"
              color="primary"
              :loading="loading"
              :aria-label="t('paylink.error.sendAgain')"
              @click="resendSms"
              class="btn-wrap"
            >
              {{ t('paylink.error.sendAgain') }}
            </v-btn>
          </v-col>
        </v-row>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import { defineComponent, ref, watch, computed } from 'vue';
import store from '@/store';
import { Sms } from '@/interfaces/sms';
import { Snackbar } from '@/interfaces/presentation/snackbar';
import { Donation } from '@/interfaces/donation/donation';
import { useLabel } from '@/composables/useLabel';
import { useValidationRules } from '@/composables/useValidationRules';
import { useSmsOperations } from '@/composables/useSmsOperations';
import { useDisplay } from 'vuetify';

export default defineComponent({
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    // keep backward-compatible 'show' prop but also support v-model (modelValue)
    modelValue: {
      type: Boolean,
      required: false,
    },
    donation: {
      type: Object as () => Donation,
      default: null,
    },
    message: {
      type: String,
      default: '',
    },
  },
  emits: ['toPaymentSelection', 'toSuccess', 'update:modelValue'],
  setup(props, { emit }) {
    // using top-level store import instead of instance proxy
    const { t } = useLabel();
    const { smAndDown } = useDisplay();
    const { rules: baseRules } = useValidationRules(t);
    const { sendPaymentRequestSms, getLatestSmsStatus } = useSmsOperations();

    const rules = {
      mobilePhoneNumber: (v: string) =>
        /^(\+46|0)[0-9]{6,14}$/.test(v) || (t('validation.mobilePhoneNumber') as string),
      requiredField: (label: string) => baseRules.value.requiredField(label),
    };

    const requiredField = (key: string) => rules.requiredField(t(key) as string);

    const errorDialog = ref(false);
    const errorMessage = ref('');
    const smsValidationWaitTime = ref(4000);
    const valid = ref(true);
    const loading = ref(false);
    const formMobileNumber = ref();
    // local copy to avoid mutating props.donation directly
    const mobileNumber = ref<string | undefined>(props.donation?.donor?.mobileNumber);
    const supportContactInfo = computed(() => t('paylink.error.supportContactInfo') as string);

    const goToPaymentMethodSelection = () => {
      emit('toPaymentSelection', true);
    };
    const isSmallScreen = smAndDown;

    const resendSms = () => {
      if (!formMobileNumber.value.validate()) {
        return;
      }

      loading.value = true;

      // resend the SMS using the local mobileNumber to avoid mutating props
      sendPaymentRequestSms(props.donation!.serialnumber, mobileNumber.value as string)
        // If we were able to resend it, then check if there is an error
        .then(() => {
          setTimeout(() => {
            getLatestSmsStatus(props.donation!.serialnumber)
              .then((sms: Sms) => {
                if (sms.errorMessage && sms.errorMessage.length > 0) {
                  errorMessage.value = sms.errorMessage;

                  const snackbar: Snackbar = {
                    active: true,
                    color: 'error',
                    text: t('paylink.error.paymentLinkCouldNotBeSent') as string,
                    timeout: 10000,
                    action: null,
                  };

                  store.commit('setSnackbar', snackbar);
                  loading.value = false;
                } else {
                  loading.value = false;
                  errorDialog.value = false;
                  emit('toSuccess', true);
                }
              })
              .catch(() => {
                errorMessage.value = t(
                  'payLinkSend.messages.payLinkSend.errorCheckPhoneNumber'
                ) as string;
                const snackbar: Snackbar = {
                  active: true,
                  color: 'error',
                  text: t('payLinkSend.messages.payLinkSend.error') as string,
                  timeout: 10000,
                  action: null,
                };
                store.commit('setSnackbar', snackbar);
                loading.value = false;
              });
          }, smsValidationWaitTime.value);
        })
        .catch(() => {
          errorMessage.value = t(
            'payLinkSend.messages.payLinkSend.probableErrorCheckPhoneNumber'
          ) as string;
          const snackbar: Snackbar = {
            active: true,
            color: 'error',
            text: t('payLinkSend.messages.payLinkSend.error') as string,
            timeout: 10000,
            action: null,
          };
          store.commit('setSnackbar', snackbar);
          loading.value = false;
        });
    };

    watch(
      () => props.show,
      (value) => {
        errorDialog.value = value;
        if (value) {
          mobileNumber.value = props.donation?.donor?.mobileNumber;
        }
      },
      { immediate: true }
    );

    watch(
      () => props.donation,
      (value) => {
        mobileNumber.value = value?.donor?.mobileNumber;
      }
    );

    // support v-model via modelValue as well
    watch(
      () => props.modelValue,
      (value) => {
        if (typeof value !== 'undefined') {
          errorDialog.value = value as boolean;
        }
      }
    );

    watch(
      () => props.message,
      (value) => {
        errorMessage.value = value;
      }
    );

    // propagate internal changes to v-model binding if used
    watch(errorDialog, (val: boolean) => {
      try {
        emit('update:modelValue', val);
      } catch {
        /* no-op */
      }
    });

    return {
      errorDialog,
      errorMessage,
      smsValidationWaitTime,
      valid,
      isSmallScreen,
      loading,
      formMobileNumber,
      mobileNumber,
      supportContactInfo,
      rules,
      t,
      requiredField,
      goToPaymentMethodSelection,
      resendSms,
    };
  },
});
</script>
<style scoped>
.body-2 {
  line-height: 20px;
}
.small-screen-dialog :deep(.v-overlay__content) {
  width: 100%;
  margin: 0;
  align-self: flex-end;
}
.font-weight-bold{
  margin: 10px 8px;
  text-wrap: auto;
  line-height: 1.2;
}
.body-wrap{
  margin: 0 8px;
  line-height: 1.2;
}
.modal-actions {
  margin: 10px 15px;
  gap: 16px;
}
.btn-wrap {
  white-space: normal;
  text-align: center;
  line-height: 1.2;
  font-weight: bold;
  letter-spacing: 0;
}
</style>
